import api from './api'
import Cookies from 'js-cookie'

export const authService = {
  // Login tradicional con email/password
  async login(email, password) {
    try {
      const response = await api.post('/auth/login', { email, password })
      if (response.data.success) {
        const { token, user } = response.data.data
        
        // Guardar token en múltiples lugares para compatibilidad
        Cookies.set('auth_token', token, { expires: 7 })
        localStorage.setItem('auth_token', token)
        localStorage.setItem('token', token) // Compatibilidad
        localStorage.setItem('user', JSON.stringify(user))
        
        return { token, user }
      }
      throw new Error(response.data.error || 'Error al iniciar sesión')
    } catch (error) {
      // Mejorar mensajes de error
      let errorMessage = 'Error al iniciar sesión'
      
      if (error.response?.data) {
        const errorData = error.response.data
        
        // Mensaje del backend
        if (errorData.error) {
          errorMessage = errorData.error
        } else if (errorData.message) {
          errorMessage = errorData.message
        }
        
        // Detalles adicionales si existen
        if (errorData.details) {
          console.error('Detalles del error:', errorData.details)
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      console.error('❌ Error en login:', {
        message: errorMessage,
        status: error.response?.status,
        response: error.response?.data
      })
      
      throw new Error(errorMessage)
    }
  },

  // Registro tradicional con email/password
  async register(userData) {
    const response = await api.post('/auth/register', userData)
    if (response.data.success) {
      const { token, user } = response.data.data
      Cookies.set('auth_token', token, { expires: 7 })
      localStorage.setItem('auth_token', token)
      localStorage.setItem('user', JSON.stringify(user))
      return { token, user }
    }
    throw new Error(response.data.error || 'Error al registrar')
  },

  // Enviar OTP a teléfono
  async sendOtp(telefono) {
    const response = await api.post('/auth/send-otp', { telefono })
    if (response.data.success) {
      return response.data
    }
    throw new Error(response.data.error || 'Error al enviar código OTP')
  },

  // Login con teléfono y OTP
  async loginWithPhone(telefono, otpCode) {
    const response = await api.post('/auth/login-phone', { telefono, otp_code: otpCode })
    if (response.data.success) {
      const { token, user } = response.data.data
      Cookies.set('auth_token', token, { expires: 7 })
      localStorage.setItem('auth_token', token)
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      return { token, user }
    }
    throw new Error(response.data.error || 'Error al iniciar sesión')
  },

  // Registro con teléfono y OTP
  async registerWithPhone(userData) {
    const response = await api.post('/auth/register-phone', userData)
    if (response.data.success) {
      const { token, user } = response.data.data
      Cookies.set('auth_token', token, { expires: 7 })
      localStorage.setItem('auth_token', token)
      localStorage.setItem('user', JSON.stringify(user))
      return { token, user }
    }
    throw new Error(response.data.error || 'Error al registrar')
  },

  // Login social (Google, Facebook, Apple)
  async socialLogin(provider, providerId, email, nombre, apellido, foto) {
    const response = await api.post('/auth/social-login', {
      provider,
      provider_id: providerId,
      email,
      nombre,
      apellido,
      foto
    })
    if (response.data.success) {
      const { token, user } = response.data.data
      Cookies.set('auth_token', token, { expires: 7 })
      localStorage.setItem('auth_token', token)
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      return { token, user }
    }
    throw new Error(response.data.error || 'Error al iniciar sesión con ' + provider)
  },

  // Solicitar recuperación de contraseña
  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email })
    if (response.data.success) {
      return response.data
    }
    throw new Error(response.data.error || 'Error al solicitar recuperación')
  },

  // Resetear contraseña con token
  async resetPassword(token, password, passwordConfirmation) {
    const response = await api.post('/auth/reset-password', {
      token,
      password,
      password_confirmation: passwordConfirmation
    })
    if (response.data.success) {
      return response.data
    }
    throw new Error(response.data.error || 'Error al restablecer contraseña')
  },

  // Cambiar contraseña (requiere autenticación)
  async changePassword(currentPassword, newPassword, newPasswordConfirmation) {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: newPasswordConfirmation
    })
    if (response.data.success) {
      return response.data
    }
    throw new Error(response.data.error || 'Error al cambiar contraseña')
  },

  // Verificar email con token
  async verifyEmail(token) {
    const response = await api.post('/auth/verify-email', { token })
    if (response.data.success) {
      return response.data
    }
    throw new Error(response.data.error || 'Error al verificar email')
  },

  // Reenviar email de verificación
  async resendVerification(email) {
    const response = await api.post('/auth/resend-verification', { email })
    if (response.data.success) {
      return response.data
    }
    throw new Error(response.data.error || 'Error al reenviar verificación')
  },

  // Agregar email a cuenta existente
  async addEmail(email) {
    const response = await api.post('/auth/add-email', { email })
    if (response.data.success) {
      return response.data
    }
    throw new Error(response.data.error || 'Error al agregar email')
  },

  // Actualizar perfil del usuario (soporta FormData para archivos)
  async updateProfile(data) {
    const response = await api.post('/auth/profile', data)
    if (response.data.success) {
      const { user } = response.data.data
      // Actualizar usuario en localStorage
      localStorage.setItem('user', JSON.stringify(user))
      return { user }
    }
    throw new Error(response.data.error || 'Error al actualizar perfil')
  },

  // Obtener información del usuario autenticado
  async getMe() {
    const response = await api.get('/auth/me')
    if (response.data.success) {
      const userData = response.data.data
      
      // Normalizar la URL del avatar/foto si existe
      if (userData) {
        const { getAvatarUrl } = await import('../utils/avatarHelpers')
        const normalizedAvatarUrl = getAvatarUrl(userData)
        if (normalizedAvatarUrl) {
          userData.avatar = normalizedAvatarUrl
          userData.foto = normalizedAvatarUrl
        }
      }
      
      return userData
    }
    throw new Error(response.data.error || 'Error al obtener usuario')
  },

  logout() {
    Cookies.remove('auth_token')
    localStorage.removeItem('auth_token')
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    window.location.href = '/login'
  },

  getToken() {
    return Cookies.get('auth_token') || localStorage.getItem('auth_token')
  },

  getUser() {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  },

  isAuthenticated() {
    return !!this.getToken()
  },
}

